import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import CollapsibleRow from '../../../common/CollapsibleRow';
import { useDebounce } from '../../../common/Debounce.hooks';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useJiraConnectContext } from '../JiraConnect.context';
import { useFindJiraIssueFields } from '../JiraConnect.hooks';
import {
  JiraApiProperties,
  JiraConnectRequestOptions,
  JiraIssueField
} from '../JiraConnect.interface';
import JiraReferenceTypeList from './JiraReferenceTypeList';
import JiraReferenceTypeListDialog from './JiraReferenceTypeListDialog';

const StyledGrid = styled(Grid)`
  align-items: center;
  justify-content: center;
  .spacer {
    flex: 1;
  }
  .actions {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const StyledJiraReferenceTypeList = styled(JiraReferenceTypeList)`
  background-color: #f7f8fc;
`;

export interface JiraReferenceDialogProps {
  open: boolean;
  onClose?: () => void;
}

const JiraReferenceDialog: React.FC<JiraReferenceDialogProps> = (
  props: JiraReferenceDialogProps
) => {
  const { open, onClose } = props;
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(25);
  const [search, setSearch] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [viewSchema, setViewSchema] = useState<string | undefined>();
  const { getJiraApi } = useJiraConnectContext();
  const debouncedSearch: string = useDebounce(search, 300);
  const [{ data, isLoading, error }, getIssueFields] = useFindJiraIssueFields();
  const [showHelp, setShowHelp] = useState(true);

  const options: Omit<JiraConnectRequestOptions, 'url'> = useMemo(() => {
    const data: Record<string, any> = {
      startAt: page * limit || 0,
      maxResults: limit
    };
    if (debouncedSearch) {
      data.query = debouncedSearch;
    }
    return { data };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    open && getJiraApi();
  }, [open, getJiraApi]);

  useEffect(() => {
    open && getIssueFields(options);
  }, [open, options, getIssueFields]);

  useEffect(() => {
    !open && setSearch('');
  }, [open]);

  useEffect(() => {
    if (!data) {
      return;
    }
    if (page * limit > data.total) {
      setPage(0);
    }
    if (data.maxResults !== limit) {
      setLimit(data.maxResults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg'>
      <DialogTitle>
        JIRA Template Reference
        {!showHelp && (
          <IconButton
            size='small'
            onClick={() => setShowHelp(true)}
            aria-label='Help'
            title='Show Help'
          >
            <HelpOutlineIcon />
          </IconButton>
        )}
        <Collapse in={showHelp}>
          <Alert onClose={() => setShowHelp(false)} severity='info'>
            The root variable passed to the template is called{' '}
            <strong>`issues`</strong>, it represents the list of Jira issues
            resulting from the current query. Each issue would have the
            following properties listed below, you can use ID column as the
            variable name in the template as you loop thru the list of issues.
            Click{' '}
            <a
              href='https://docxtemplater.com/demo/#loops'
              target='_blank'
              rel='noreferrer'
            >
              here
            </a>{' '}
            to learn more.
          </Alert>
        </Collapse>
      </DialogTitle>
      <Box mx={3} my={1}>
        <TextField
          fullWidth
          variant='outlined'
          placeholder='Search issue field'
          onChange={event => setSearch(event.target.value)}
        />
      </Box>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={1} />
              <TableCell>Name</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!data?.values.length && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant='body1' color='textSecondary'>
                    {isLoading ? 'Loading...' : 'No issue fields available.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {data?.values.map((issueField: JiraIssueField) => {
              const isSelected: boolean = selectedId === issueField.id;
              return (
                <JiraReferenceListItem
                  key={issueField.id}
                  issueField={issueField}
                  isSelected={isSelected}
                  onClickType={setViewSchema}
                  onIconClick={() => {
                    setSelectedId(!isSelected ? issueField.id : undefined);
                  }}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading && (
        <Box>
          <LinearProgress />
        </Box>
      )}
      <StyledGrid container>
        <Grid item className='actions'>
          <Button variant='outlined' onClick={onClose}>
            Close
          </Button>
        </Grid>
        <div className='spacer' />
        <Grid item>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component='div'
            count={data?.total || 0}
            rowsPerPage={data?.maxResults || 25}
            page={page * limit > (data?.total || 0) ? 0 : page}
            onPageChange={(_, page: number) => setPage(page)}
            onRowsPerPageChange={(
              event: React.ChangeEvent<HTMLInputElement>
            ) => {
              setLimit(+event.target.value);
              setPage(0);
            }}
          />
        </Grid>
      </StyledGrid>

      <Collapse in={!!error} style={{ minHeight: 'unset' }}>
        <Box mx={3} my={1}>
          <Alert severity='error'>
            {error?.message || 'An error occurred.'}
          </Alert>
        </Box>
      </Collapse>

      <JiraReferenceTypeListDialog
        schemaKey={viewSchema}
        onClickType={setViewSchema}
        onClose={() => setViewSchema(undefined)}
      />
    </Dialog>
  );
};

export default JiraReferenceDialog;

export interface JiraReferenceListItemProps {
  issueField: JiraIssueField;
  isSelected?: boolean;
  onIconClick?: () => void;
  onClickType?: (schemaKey: string) => void;
}

export const JiraReferenceListItem: React.FC<JiraReferenceListItemProps> = (
  props: JiraReferenceListItemProps
) => {
  const { issueField, isSelected = false, onIconClick, onClickType } = props;
  const [hasProperties, setHasProperties] = useState<boolean>(false);

  const type: string = useMemo(() => {
    const { type = '', items = '' } = issueField.schema || {};
    if (type && items) {
      return `${type} (${items})`;
    } else if (!type && items) {
      return items;
    }
    return type;
  }, [issueField]);

  return (
    <CollapsibleRow
      colSpan={5}
      open={isSelected}
      disabled={!hasProperties}
      collapsedContent={
        <StyledJiraReferenceTypeList
          show={isSelected}
          keyOrIssueField={issueField}
          onClickType={onClickType}
          onFindProperties={(properties?: JiraApiProperties) => {
            setHasProperties(!!properties);
          }}
        />
      }
      onIconClick={onIconClick}
      rowProps={{
        hover: hasProperties,
        onClick: () => {
          if (hasProperties) {
            onIconClick?.();
          }
        }
      }}
    >
      <TableCell>{issueField.name}</TableCell>
      <TableCell>{issueField.id}</TableCell>
      <TableCell title={issueField.schema?.system}>
        <code>{type}</code>
      </TableCell>
      <WrappedTableCell>{issueField.description}</WrappedTableCell>
    </CollapsibleRow>
  );
};
